import * as Sentry from '@sentry/nextjs';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Organization } from 'scribe-web-shared/types/organization';
import PermissionLevel from 'v2/components/modals/InviteReferralModal/PermissionLevel';

import teamService from '../services/teamService';

const useMultiOrgAccessRequestList = (organizations: Organization[]) => {
  const enabledPaths = ['/workspace', '/teams'];
  const orgIds = organizations?.map((org) => org.id);
  const enabledAccessRequestList = orgIds?.length > 0 && enabledPaths.some((path) => window.location.pathname.startsWith(path));
  const queryCache = useQueryClient();

  const {
    data: accessRequests,
    isLoading,
    error
  } = useQuery(
    // Need to sort orgIds to ensure they are always in the same order for cacheing
    ['accessRequestList', [...(orgIds?.length ? orgIds : [])].sort()],
    () => {
      const accessRequestData = Promise.all(
        orgIds
          ?.filter((orgId: string) => organizations.find((org) => org.id === orgId)?.permission_level === 'admin')
          ?.map(async (orgId: string) => {
            try {
              const requests = await teamService.listAccessRequests(orgId);
              const count = requests?.length ?? 0;

              return { orgId, requests, count };
            } catch (e) {
              Sentry.captureException(e);
              return { orgId, requests: [], count: 0 };
            }
          })
      );

      return accessRequestData;
    },
    {
      enabled: enabledAccessRequestList,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true
    }
  );

  const { mutateAsync: acceptRequest } = useMutation(
    async (variables: {
      activeTeamId: string;
      email: string;
      permissionLevel: PermissionLevel;
      successCallback?: (x?) => void;
      errorCallback?: (e?) => void;
    }) => {
      return teamService.acceptRequest(variables.activeTeamId, variables.email, variables.permissionLevel);
    },
    {
      onSuccess: (data, variables) => {
        queryCache.invalidateQueries(['accessRequestList']);
        variables.successCallback?.(data);
      },
      onError: (err, variables) => {
        variables.errorCallback?.(err);
      }
    }
  );

  const { mutateAsync: bulkAcceptRequest } = useMutation(
    async (variables: {
      requests: { email: string; organization: string; permission_level: PermissionLevel }[];
      successCallback?: (data?) => void;
      errorCallback?: (error?) => void;
    }) => {
      return teamService.bulkAcceptRequest(variables.requests);
    },
    {
      onSuccess: (data, variables) => {
        queryCache.invalidateQueries(['accessRequestList']);
        variables.successCallback?.(data);
      },
      onError: (err, variables) => {
        variables.errorCallback?.(err);
      }
    }
  );

  const { mutateAsync: rejectRequest } = useMutation(
    (variables: { activeTeamId: string; email: string; successCallback?: (x?: any) => void; errorCallback?: (e?: any) => void }) => {
      return teamService.rejectRequest(variables.activeTeamId, variables.email);
    },
    {
      onSuccess: (data, variables) => {
        queryCache.invalidateQueries(['accessRequestList']);
        variables.successCallback?.(data);
      },
      onError: (err, variables) => {
        variables.errorCallback?.(err);
      }
    }
  );

  const { mutateAsync: bulkRejectRequest } = useMutation(
    (variables: {
      requests: { email: string; organization: string }[];
      successCallback?: (x?: any) => void;
      errorCallback?: (e?: any) => void;
    }) => {
      return teamService.bulkRejectRequest(variables.requests);
    },
    {
      onSuccess: (data, variables) => {
        queryCache.invalidateQueries(['accessRequestList']);
        variables.successCallback?.(data);
      },
      onError: (err, variables) => {
        variables.errorCallback?.(err);
      }
    }
  );

  return { accessRequests, isLoading, error, acceptRequest, bulkAcceptRequest, rejectRequest, bulkRejectRequest };
};

export default useMultiOrgAccessRequestList;
